import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  input,
  model,
  OnDestroy,
  signal,
  ViewChild,
  TemplateRef,
} from '@angular/core';
import {
  MatButtonModule,
  MatIconButton,
  MatMiniFabButton,
} from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'sia-file-preview-image',
  host: {
    class: 'block h-full w-full',
  },
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatIconButton,
    MatMiniFabButton,
  ],
  templateUrl: './file-preview-image.component.html',
  styleUrl: './file-preview-image.component.scss',
})
export class FilePreviewImageComponent implements OnDestroy, AfterViewInit {
  imagesUrls = model<string[]>([]);
  horizontal = input<boolean>(false);
  fullscreen = model<boolean>(false);
  actionsTemplate = input<TemplateRef<any>>();
  previewImagesUrls = computed(() => {
    return this.imagesUrls().filter((x) => x.includes('_small'));
  });
  fullImagesUrls = computed(() => {
    return this.imagesUrls().filter((x) => !x.includes('_small'));
  });

  // Gallery state
  currentImageIndex = signal(0);
  zoomLevel = signal(1);
  isPanning = signal(false);
  panStart = signal({ x: 0, y: 0 });
  panOffset = signal({ x: 0, y: 0 });

  @ViewChild('thumbnailStrip') thumbnailStrip?: ElementRef<HTMLDivElement>;
  @ViewChild('imageContainer') imageContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('imageElement') imageElement?: ElementRef<HTMLImageElement>;
  canScrollThumbnailsLeft = signal(false);
  canScrollThumbnailsRight = signal(false);

  constructor() {
    effect(() => {
      this.previewImagesUrls();
      queueMicrotask(() => this.updateThumbnailScrollState());
    });
  }

  // Computed properties
  currentImage = computed(() => {
    const images = this.fullImagesUrls();
    const index = this.currentImageIndex();
    return images[index] || '';
  });

  hasMultipleImages = computed(() => this.previewImagesUrls().length > 1);
  hasPreviousImage = computed(() => this.currentImageIndex() > 0);
  hasNextImage = computed(
    () => this.currentImageIndex() < this.previewImagesUrls().length - 1,
  );

  closeGallery() {
    this.fullscreen.set(false);
    this.resetZoom();
    document.body.style.overflow = 'auto';
  }

  nextImage() {
    if (this.hasNextImage()) {
      this.currentImageIndex.update((index) => index + 1);
      this.resetZoom();
    }
  }

  previousImage() {
    if (this.hasPreviousImage()) {
      this.currentImageIndex.update((index) => index - 1);
      this.resetZoom();
    }
  }

  // Thumbnail strip methods
  scrollThumbnailsLeft() {
    this.scrollThumbnails('left');
  }

  scrollThumbnailsRight() {
    this.scrollThumbnails('right');
  }

  // Zoom methods
  zoomIn() {
    this.zoomLevel.update((level) => Math.min(level + 0.25, 3));
    this.constrainPanWithinBounds();
  }

  zoomOut() {
    this.zoomLevel.update((level) => Math.max(level - 0.25, 0.5));
    this.constrainPanWithinBounds();
  }

  resetZoom() {
    this.zoomLevel.set(1);
    this.panOffset.set({ x: 0, y: 0 });
  }

  // Pan methods
  startPan(event: MouseEvent | TouchEvent) {
    if (this.zoomLevel() <= 1) return;

    event.preventDefault();
    this.isPanning.set(true);
    const clientX =
      'touches' in event ? event.touches[0].clientX : event.clientX;
    const clientY =
      'touches' in event ? event.touches[0].clientY : event.clientY;

    this.panStart.set({
      x: clientX - this.panOffset().x,
      y: clientY - this.panOffset().y,
    });

    // Add mouse move listener to document for better panning experience
    if (!('touches' in event)) {
      document.addEventListener('mousemove', this.onMouseMove);
      document.addEventListener('mouseup', this.onMouseUp);
    }
  }

  private onMouseMove = (event: MouseEvent) => {
    if (!this.isPanning() || this.zoomLevel() <= 1) return;

    event.preventDefault();
    this.panOffset.set(
      this.clampPanOffset({
        x: event.clientX - this.panStart().x,
        y: event.clientY - this.panStart().y,
      }),
    );
  };

  private onMouseUp = () => {
    this.stopPan();
  };

  onPan(event: MouseEvent | TouchEvent) {
    if (!this.isPanning() || this.zoomLevel() <= 1) return;

    event.preventDefault();
    const clientX =
      'touches' in event ? event.touches[0].clientX : event.clientX;
    const clientY =
      'touches' in event ? event.touches[0].clientY : event.clientY;

    this.panOffset.set(
      this.clampPanOffset({
        x: clientX - this.panStart().x,
        y: clientY - this.panStart().y,
      }),
    );
  }

  stopPan() {
    this.isPanning.set(false);
    // Remove mouse move listeners from document
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
  }

  // Wheel zoom
  onWheel(event: WheelEvent) {
    if (event.ctrlKey) {
      event.preventDefault();
      if (event.deltaY < 0) {
        this.zoomIn();
      } else {
        this.zoomOut();
      }
    } else if (this.zoomLevel() > 1) {
      event.preventDefault();
      let dx = event.deltaX;
      let dy = event.deltaY;

      // Ensure shift+scroll maps to horizontal pan if the browser didn't do it natively
      if (event.shiftKey && dy !== 0 && dx === 0) {
        dx = dy;
        dy = 0;
      }

      this.panOffset.update((offset) =>
        this.clampPanOffset({
          x: offset.x - dx,
          y: offset.y - dy,
        }),
      );
    }
  }

  // Keyboard navigation
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (!this.imagesUrls().length) return;

    switch (event.key) {
      case 'Escape':
        this.closeGallery();
        break;
      case 'ArrowLeft':
        this.previousImage();
        break;
      case 'ArrowRight':
        this.nextImage();
        break;
      case '+':
      case '=':
        this.zoomIn();
        break;
      case '-':
        this.zoomOut();
        break;
      case '0':
        this.resetZoom();
        break;
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.updateThumbnailScrollState();
    this.constrainPanWithinBounds();
  }

  ngAfterViewInit() {
    queueMicrotask(() => this.updateThumbnailScrollState());
  }

  onThumbnailScroll() {
    this.updateThumbnailScrollState();
  }

  private scrollThumbnails(direction: 'left' | 'right') {
    const strip = this.thumbnailStrip?.nativeElement;
    if (!strip) return;

    const amount = strip.clientWidth ? strip.clientWidth * 0.8 : 200;
    const delta = direction === 'left' ? -amount : amount;
    strip.scrollBy({ left: delta, behavior: 'smooth' });
    requestAnimationFrame(() => this.updateThumbnailScrollState());
  }

  private updateThumbnailScrollState() {
    const strip = this.thumbnailStrip?.nativeElement;
    if (!strip) {
      this.canScrollThumbnailsLeft.set(false);
      this.canScrollThumbnailsRight.set(false);
      return;
    }

    const { scrollLeft, clientWidth, scrollWidth } = strip;
    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);
    const epsilon = 1;

    this.canScrollThumbnailsLeft.set(scrollLeft > epsilon);
    this.canScrollThumbnailsRight.set(scrollLeft < maxScrollLeft - epsilon);
  }

  // Image transform style
  getImageTransform() {
    const zoom = this.zoomLevel();
    const pan = this.panOffset();
    return `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`;
  }

  onImageLoad() {
    this.constrainPanWithinBounds();
  }

  private constrainPanWithinBounds() {
    this.panOffset.update((offset) => this.clampPanOffset(offset));
  }

  private clampPanOffset(offset: { x: number; y: number }) {
    const bounds = this.getPanBounds();
    return {
      x: this.clamp(offset.x, -bounds.maxX, bounds.maxX),
      y: this.clamp(offset.y, -bounds.maxY, bounds.maxY),
    };
  }

  private getPanBounds() {
    const container = this.imageContainer?.nativeElement;
    const image = this.imageElement?.nativeElement;
    const zoom = this.zoomLevel();

    if (!container || !image || zoom <= 1) {
      return { maxX: 0, maxY: 0 };
    }

    const containerRect = container.getBoundingClientRect();
    const naturalWidth = image.naturalWidth;
    const naturalHeight = image.naturalHeight;

    if (!naturalWidth || !naturalHeight) {
      return { maxX: 0, maxY: 0 };
    }

    const containerAspect = containerRect.width / containerRect.height;
    const imageAspect = naturalWidth / naturalHeight;

    let baseWidth = containerRect.width;
    let baseHeight = containerRect.height;

    if (imageAspect > containerAspect) {
      baseHeight = baseWidth / imageAspect;
    } else {
      baseWidth = baseHeight * imageAspect;
    }

    const scaledWidth = baseWidth * zoom;
    const scaledHeight = baseHeight * zoom;

    return {
      maxX: Math.max(0, (scaledWidth - containerRect.width) / 2),
      maxY: Math.max(0, (scaledHeight - containerRect.height) / 2),
    };
  }

  private clamp(value: number, min: number, max: number) {
    if (value < min) return min;
    if (value > max) return max;
    return value;
  }

  ngOnDestroy() {
    // Clean up any active event listeners
    this.stopPan();
  }
}
